import React, { useState } from 'react';
import { Box, Grid, ButtonBase, useTheme } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useTranslation } from 'react-i18next';

import { useSettingsContext } from '../../../../setting/SettingsContext';
import {
    PRESETS,
    PRESETS_LIST,
    CUSTOM_PRESET_KEY,
    buildPresetFromHex,
} from '../../../../setting/presets';
import SectionLabel from './SectionLabel';
import CustomColorDialog from './CustomColorDialog';

/**
 * 6 built-in color-preset cards + a 7th tile for the custom hex picker.
 *
 * Built-in click → onChangeColorPresets → settings.themeColorPresets
 *   updates → SettingsProvider rebuilds the MUI theme.
 * Custom tile click → opens CustomColorDialog. On confirm,
 *   onChangeCustomColor sets themeColorPresets='custom' and saves the
 *   hex into themeCustomColor.
 */
const PresetsOptions = () => {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const {
        themeColorPresets,
        themeCustomColor,
        onChangeColorPresets,
        onChangeCustomColor,
    } = useSettingsContext();

    const [dialogOpen, setDialogOpen] = useState(false);

    const customPreset = buildPresetFromHex(themeCustomColor);
    const customSelected = themeColorPresets === CUSTOM_PRESET_KEY;

    return (
        <Box>
            <SectionLabel>{isArabic ? 'الألوان' : 'Presets'}</SectionLabel>
            <Grid container spacing={1.25}>
                {PRESETS_LIST.map((key) => {
                    const preset = PRESETS[key];
                    const selected = themeColorPresets === key;
                    return (
                        <Grid item xs={4} key={key}>
                            <ButtonBase
                                onClick={() => onChangeColorPresets(key)}
                                aria-label={`${isArabic ? preset.labelAr : preset.name} preset`}
                                sx={{
                                    width: '100%',
                                    aspectRatio: '1.4 / 1',
                                    borderRadius: 2,
                                    border: `1.5px solid ${
                                        selected ? preset.main : theme.palette.divider
                                    }`,
                                    bgcolor: selected ? `${preset.main}14` : 'transparent',
                                    transition: 'all 0.2s',
                                    position: 'relative',
                                    '&:hover': {
                                        borderColor: preset.main,
                                        bgcolor: `${preset.main}10`,
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 22,
                                        height: 28,
                                        borderRadius: 1,
                                        bgcolor: preset.main,
                                        boxShadow: `0 4px 8px 0 ${preset.main}55`,
                                    }}
                                />
                                {selected && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            insetInlineEnd: 4,
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            bgcolor: preset.main,
                                            color: preset.contrastText,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CheckRoundedIcon sx={{ fontSize: 12 }} />
                                    </Box>
                                )}
                            </ButtonBase>
                        </Grid>
                    );
                })}

                {/* Custom hex tile */}
                <Grid item xs={4}>
                    <ButtonBase
                        onClick={() => setDialogOpen(true)}
                        aria-label={isArabic ? 'اختيار لون مخصص' : 'Pick custom color'}
                        sx={{
                            width: '100%',
                            aspectRatio: '1.4 / 1',
                            borderRadius: 2,
                            border: `1.5px dashed ${
                                customSelected
                                    ? (customPreset?.main || theme.palette.primary.main)
                                    : theme.palette.divider
                            }`,
                            bgcolor: customSelected && customPreset
                                ? `${customPreset.main}14`
                                : 'transparent',
                            transition: 'all 0.2s',
                            position: 'relative',
                            color: customSelected && customPreset
                                ? customPreset.main
                                : 'text.secondary',
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                bgcolor: `${theme.palette.primary.main}08`,
                            },
                        }}
                    >
                        {customPreset ? (
                            <Box
                                sx={{
                                    width: 22,
                                    height: 28,
                                    borderRadius: 1,
                                    bgcolor: customPreset.main,
                                    boxShadow: `0 4px 8px 0 ${customPreset.main}55`,
                                }}
                            />
                        ) : (
                            <AddRoundedIcon fontSize="small" />
                        )}

                        {/* Edit affordance — small pencil overlay so users
                            know the tile re-opens the dialog after first pick */}
                        {customPreset && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 4,
                                    insetInlineStart: 4,
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: 'background.paper',
                                    border: (t) => `1px solid ${t.palette.divider}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <EditRoundedIcon sx={{ fontSize: 10 }} />
                            </Box>
                        )}

                        {customSelected && customPreset && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 4,
                                    insetInlineEnd: 4,
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    bgcolor: customPreset.main,
                                    color: customPreset.contrastText,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CheckRoundedIcon sx={{ fontSize: 12 }} />
                            </Box>
                        )}
                    </ButtonBase>
                </Grid>
            </Grid>

            <CustomColorDialog
                open={dialogOpen}
                initialHex={themeCustomColor}
                onClose={() => setDialogOpen(false)}
                onConfirm={(hex) => onChangeCustomColor(hex)}
            />
        </Box>
    );
};

export default PresetsOptions;
